import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Plus, Minus, X, Store, MapPin, Truck, ImageIcon, Flame, Star, CreditCard, Wallet, CalendarClock, MessageCircle, PhoneCall, Mail, ChevronDown, SlidersHorizontal, Banknote } from "lucide-react";
import api, { apiError, fileUrl } from "@/lib/api";
import { money, TIME_SLOTS, nextDays } from "@/lib/orderStatus";
import { applyStoreLanguage } from "@/i18n";
import { getMyOrdersForSlug, saveMyOrder } from "@/lib/myOrders";
import { Package } from "lucide-react";
import CustomerAssistant from "@/components/CustomerAssistant";
import { Brand } from "@/components/Brand";
import ProductCard from "@/components/ProductCard";
import ProductDetailSheet from "@/components/ProductDetailSheet";
import PhoneVerify from "@/components/PhoneVerify";
import PaymentPanel from "@/components/PaymentPanel";
import PaymentSuccess from "@/components/PaymentSuccess";
import { ImageLightbox } from "@/components/ImageLightbox";

export default function Storefront() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const myRecentOrders = getMyOrdersForSlug(slug);
  const [cart, setCart] = useState({});
  const [checkout, setCheckout] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [detail, setDetail] = useState(null);
  const [sort, setSort] = useState("recent");
  const [inStockOnly, setInStockOnly] = useState(false);
  const [activeCat, setActiveCat] = useState("all");
  const [ordersOpen, setOrdersOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["store", slug],
    queryFn: async () => (await api.get(`/public/store/${slug}`)).data,
  });

  const products = useMemo(() => data?.products || [], [data]);
  const business = data?.business;
  const currency = business?.currency || "XOF";

  useEffect(() => {
    if (!business) return;
    const stored = business.multilingual ? localStorage.getItem(`sn_store_lang_${slug}`) : null;
    applyStoreLanguage(stored || business.language || "fr");
  }, [business, slug]);

  // Ancien lien / variante de slug → redirige proprement vers le lien actuel (URL canonique)
  useEffect(() => {
    if (business?.slug && business.slug !== slug) {
      navigate(`/boutique/${business.slug}`, { replace: true });
    }
  }, [business, slug, navigate]);

  const pickStoreLang = (code) => {
    localStorage.setItem(`sn_store_lang_${slug}`, code);
    applyStoreLanguage(code);
  };
  const curLang = (i18n.language || "fr").startsWith("en") ? "en" : "fr";

  const productById = useMemo(() => Object.fromEntries(products.map((p) => [p.product_id, p])), [products]);
  const ckey = (pid, size) => `${pid}__${size || ""}`;

  const cartItems = useMemo(
    () => Object.entries(cart).map(([key, ci]) => {
      const p = productById[ci.product_id];
      return p ? { ...p, size: ci.size, qty: ci.qty, key } : null;
    }).filter(Boolean),
    [cart, productById]
  );
  const total = cartItems.reduce((s, p) => s + p.price * p.qty, 0);
  const count = cartItems.reduce((s, p) => s + p.qty, 0);
  const qtyForProduct = (pid) => Object.values(cart).filter((c) => c.product_id === pid).reduce((s, c) => s + c.qty, 0);
  const qtyForSize = (pid, size) => Object.values(cart).filter((c) => c.product_id === pid && c.size === (size || "")).reduce((s, c) => s + c.qty, 0);
  const stockForSize = (p, size) => ((p.sizes?.length || 0) > 0 && Object.keys(p.size_stock || {}).length) ? (p.size_stock[size] || 0) : p.stock_qty;

  // Ajout au panier (depuis la fiche produit, avec taille). La taille est obligatoire si le produit en propose.
  const addToCart = (p, qty = 1, size = "") => {
    if ((p.sizes?.length || 0) > 0 && !size) { toast.error(t("checkout.sizeRequired")); return; }
    const addN = Math.max(1, qty);
    if (qtyForSize(p.product_id, size) + addN > stockForSize(p, size)) { toast.error(t("checkout.maxStock")); return; }
    const key = ckey(p.product_id, size);
    const cur = cart[key]?.qty || 0;
    setCart({ ...cart, [key]: { product_id: p.product_id, size: size || "", qty: cur + addN } });
    toast.success(t("checkout.addedToCart", { name: `${p.name}${size ? ` (${size})` : ""}` }));
  };
  // Cross-sell : si le produit a des tailles, ouvrir la fiche pour choisir ; sinon ajout direct.
  const quickAdd = (p) => { if ((p.sizes?.length || 0) > 0) setDetail(p); else addToCart(p, 1, ""); };
  const changeQty = (key, delta) => {
    setCart((c) => {
      const it = c[key]; if (!it) return c;
      const p = productById[it.product_id];
      if (delta > 0 && p && qtyForSize(it.product_id, it.size) + 1 > stockForSize(p, it.size)) { toast.error(t("checkout.maxStock")); return c; }
      const nextQty = it.qty + delta;
      const next = { ...c };
      if (nextQty <= 0) delete next[key]; else next[key] = { ...it, qty: nextQty };
      return next;
    });
  };
  const removeLine = (key) => setCart((c) => { const next = { ...c }; delete next[key]; return next; });

  const categories = useMemo(() => [...new Set(products.map((p) => p.category).filter(Boolean))], [products]);

  const shownProducts = useMemo(() => {
    let list = [...products];
    if (activeCat !== "all") list = list.filter((p) => p.category === activeCat);
    if (inStockOnly) list = list.filter((p) => p.stock_qty > 0);
    if (sort === "price_asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price_desc") list.sort((a, b) => b.price - a.price);
    return list;
  }, [products, sort, inStockOnly, activeCat]);

  if (isLoading) return <Center>{t("store.loading")}</Center>;
  if (isError || !business) return <Center>{t("store.notFound")}</Center>;

  return (
    <div className="min-h-screen bg-white">
      {/* Bandeau promo */}
      <div className="bg-[#1A1A1A] text-white text-center py-2 px-4" data-testid="promo-banner">
        <p className="font-jost text-[11px] md:text-xs uppercase tracking-[0.15em]">{t("store.officialBoutique")} · {business.city || t("store.trackedDelivery")}</p>
      </div>
      {/* En-tête épuré */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[#ededed]">
        <div className="max-w-lg md:max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          {business.multilingual ? (
            <div className="flex items-center rounded-full border border-[#E5E5E5] overflow-hidden h-8" data-testid="store-lang-switcher">
              {["fr", "en"].map((code) => (
                <button
                  key={code}
                  data-testid={`store-lang-${code}`}
                  onClick={() => pickStoreLang(code)}
                  className={`px-2.5 h-full text-[11px] font-jost uppercase tracking-wider transition-colors ${curLang === code ? "bg-[#1A1A1A] text-white" : "text-[#666]"}`}
                >
                  {code}
                </button>
              ))}
            </div>
          ) : (
            <span className="w-10" />
          )}
          <h1 className="font-serif-display text-2xl md:text-3xl font-semibold tracking-tight text-[#1A1A1A] text-center truncate px-2" data-testid="store-name">{business.name}</h1>
          <button onClick={() => count > 0 && setCheckout(true)} className="w-10 h-10 flex items-center justify-center relative" data-testid="header-cart-btn">
            <ShoppingCart className="h-5 w-5 text-[#1A1A1A]" />
            {count > 0 && <span className="absolute top-0 right-0 bg-black text-white text-[10px] h-4 min-w-[16px] px-1 rounded-full flex items-center justify-center" data-testid="header-cart-count">{count}</span>}
          </button>
        </div>
      </header>

      <div className="max-w-lg md:max-w-3xl lg:max-w-5xl xl:max-w-6xl mx-auto pb-32">
        <div className="px-4 md:px-6 pt-6">
          {business.description && <p className="font-jost text-sm md:text-base text-[#666] leading-relaxed text-center max-w-2xl mx-auto mb-6" data-testid="store-description">{business.description}</p>}

          {myRecentOrders.length > 0 && (
            <div className="bg-[#1B4332] text-white rounded-2xl shadow-sm p-4 mb-5" data-testid="my-orders-banner">
              {myRecentOrders.length === 1 ? (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <Package className="h-5 w-5 text-[#FFBA08]" />
                    <p className="font-display font-bold text-sm">{t("store.followOne")}</p>
                  </div>
                  <button
                    data-testid={`my-order-${myRecentOrders[0].orderId}`}
                    onClick={() => navigate(`/commande/${myRecentOrders[0].orderId}`)}
                    className="w-full flex items-center justify-between bg-white/10 hover:bg-white/15 rounded-xl px-3 py-2.5 text-left transition-colors active:scale-[0.99]"
                  >
                    <span className="text-sm font-semibold">{t("store.orderNum", { id: myRecentOrders[0].orderId.slice(-6) })}</span>
                    <span className="text-xs text-[#FFBA08] font-bold">{t("store.viewTracking")}</span>
                  </button>
                </>
              ) : (
                <>
                  <button
                    data-testid="my-orders-toggle"
                    onClick={() => setOrdersOpen((v) => !v)}
                    className="w-full flex items-center gap-2 text-left"
                    aria-expanded={ordersOpen}
                  >
                    <Package className="h-5 w-5 text-[#FFBA08]" />
                    <p className="font-display font-bold text-sm flex-1">{t("store.followManyCount", { count: myRecentOrders.length })}</p>
                    <ChevronDown className={`h-5 w-5 text-[#FFBA08] transition-transform ${ordersOpen ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence initial={false}>
                    {ordersOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="space-y-2 mt-3 max-h-64 overflow-y-auto no-scrollbar">
                          {myRecentOrders.map((o) => (
                            <button
                              key={o.orderId}
                              data-testid={`my-order-${o.orderId}`}
                              onClick={() => navigate(`/commande/${o.orderId}`)}
                              className="w-full flex items-center justify-between bg-white/10 hover:bg-white/15 rounded-xl px-3 py-2.5 text-left transition-colors active:scale-[0.99]"
                            >
                              <span className="text-sm font-semibold">{t("store.orderNum", { id: o.orderId.slice(-6) })}</span>
                              <span className="text-xs text-[#FFBA08] font-bold">{t("store.viewTracking")}</span>
                            </button>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
            </div>
          )}

          {business.reviews_summary?.count > 0 && (
            <div className="bg-white rounded-2xl border border-black/5 shadow-sm p-4 mb-5" data-testid="reviews-block">
              <div className="flex items-center gap-3 mb-3">
                <div className="text-center">
                  <p className="font-display text-3xl font-extrabold text-[#1A1A1A] leading-none" data-testid="reviews-average">{business.reviews_summary.average.toFixed(1)}</p>
                  <div className="flex gap-0.5 mt-1">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`h-3.5 w-3.5 ${Math.round(business.reviews_summary.average) >= n ? "fill-[#FFBA08] text-[#FFBA08]" : "text-[#E5E0D8]"}`} />
                    ))}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{t("store.reviews")}</p>
                  <p className="text-xs text-[#9c968a]" data-testid="reviews-count">{t("store.verifiedReviews", { count: business.reviews_summary.count })}</p>
                </div>
              </div>
              <div className="space-y-2">
                {business.reviews_summary.samples.map((r, i) => (
                  <div key={r.created_at || `rev-${i}`} className="bg-[#F9F6F0] rounded-xl p-3">
                    <div className="flex items-center gap-1 mb-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`h-3 w-3 ${r.rating >= n ? "fill-[#FFBA08] text-[#FFBA08]" : "text-[#E5E0D8]"}`} />
                      ))}
                      <span className="text-xs font-semibold text-[#5C574F] ml-1">{r.customer_name || t("store.customer")}</span>
                    </div>
                    <p className="text-sm text-[#5C574F] leading-snug">« {r.comment} »</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="lg:flex lg:gap-10">
            {/* Menu catégories — desktop */}
            {categories.length > 0 && (
              <aside className="hidden lg:block lg:w-44 shrink-0" data-testid="category-sidebar">
                <h3 className="font-serif-display text-lg text-[#1A1A1A] mb-3">{t("store.categories")}</h3>
                <ul className="space-y-1.5">
                  <li>
                    <button data-testid="cat-all" onClick={() => setActiveCat("all")} className={`font-jost text-sm uppercase tracking-wider ${activeCat === "all" ? "text-[#1A1A1A] font-semibold" : "text-[#999] hover:text-[#1A1A1A]"}`}>{t("store.seeAll")}</button>
                  </li>
                  {categories.map((c) => (
                    <li key={c}>
                      <button data-testid={`cat-${c}`} onClick={() => setActiveCat(c)} className={`font-jost text-sm uppercase tracking-wider ${activeCat === c ? "text-[#1A1A1A] font-semibold" : "text-[#999] hover:text-[#1A1A1A]"}`}>{c}</button>
                    </li>
                  ))}
                </ul>
              </aside>
            )}

            <div className="flex-1 min-w-0">
              {/* Catégories — mobile (pills) */}
              {categories.length > 0 && (
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 lg:hidden" data-testid="category-pills">
                  <button onClick={() => setActiveCat("all")} className={`shrink-0 px-3 h-8 rounded-full font-jost text-xs uppercase tracking-wider border ${activeCat === "all" ? "bg-black text-white border-black" : "border-[#E5E5E5] text-[#666]"}`}>{t("store.all")}</button>
                  {categories.map((c) => (
                    <button key={c} data-testid={`cat-pill-${c}`} onClick={() => setActiveCat(c)} className={`shrink-0 px-3 h-8 rounded-full font-jost text-xs uppercase tracking-wider border ${activeCat === c ? "bg-black text-white border-black" : "border-[#E5E5E5] text-[#666]"}`}>{c}</button>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between border-y border-[#E5E5E5] py-3 mb-6" data-testid="shop-toolbar">
                <p className="font-jost text-xs md:text-sm text-[#666]" data-testid="product-count">{t("store.articles", { count: shownProducts.length })}</p>
                <div className="flex items-center gap-4">
                  <button data-testid="filter-stock-btn" onClick={() => setInStockOnly((v) => !v)} className={`font-jost text-xs uppercase tracking-wider inline-flex items-center gap-1.5 ${inStockOnly ? "text-[#1A1A1A]" : "text-[#999]"}`}>
                    <SlidersHorizontal className="h-3.5 w-3.5" /> {inStockOnly ? t("store.inStock") : t("store.filter")}
                  </button>
                  <div className="relative">
                    <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="font-jost text-xs uppercase tracking-wider bg-transparent appearance-none pr-5 outline-none cursor-pointer text-[#1A1A1A]">
                      <option value="recent">{t("store.sortRecent")}</option>
                      <option value="price_asc">{t("store.priceAsc")}</option>
                      <option value="price_desc">{t("store.priceDesc")}</option>
                    </select>
                    <ChevronDown className="h-3.5 w-3.5 absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-8 md:gap-x-6 md:gap-y-12 items-stretch">
                {shownProducts.map((p, i) => (
                  <ProductCard key={p.product_id} product={p} currency={currency} onOpen={setDetail} index={i} />
                ))}
              </div>
            </div>
          </div>

          {count > 0 && (() => {
            const suggestions = products.filter((p) => p.stock_qty > 0 && !qtyForProduct(p.product_id)).slice(0, 6);
            if (suggestions.length === 0) return null;
            return (
              <div className="mt-7" data-testid="crosssell-banner">
                <h3 className="font-display text-lg font-bold mb-1">{t("store.completeCart")}</h3>
                <p className="text-xs text-[#9c968a] mb-3">{t("store.customersAdd")}</p>
                <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4">
                  {suggestions.map((p) => (
                    <div key={p.product_id} className="shrink-0 w-32 bg-white rounded-xl border border-black/5 shadow-sm overflow-hidden" data-testid={`suggestion-${p.product_id}`}>
                      <div className="h-24 bg-[#F9F6F0] flex items-center justify-center overflow-hidden">
                        {p.image_url ? (
                          <img
                            src={fileUrl(p.image_url)}
                            alt={p.name}
                            loading="lazy"
                            decoding="async"
                            onClick={() => setLightbox({ src: fileUrl(p.image_url), alt: p.name })}
                            data-testid={`suggestion-image-${p.product_id}`}
                            className="h-full w-full object-cover cursor-zoom-in"
                          />
                        ) : <Store className="h-6 w-6 text-[#cfc8ba]" />}
                      </div>
                      <div className="p-2">
                        <p className="text-xs font-semibold leading-tight line-clamp-2 min-h-[2rem]">{p.name}</p>
                        <p className="text-xs font-bold text-[#E85D04] mt-0.5">{money(p.price, currency)}</p>
                        <button data-testid={`suggestion-add-${p.product_id}`} onClick={() => quickAdd(p)} className="w-full h-8 mt-1.5 rounded-lg bg-[#1B4332] text-white text-xs font-semibold active:scale-95 transition-transform">{(p.sizes?.length || 0) > 0 ? t("store.see") : t("store.add")}</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pb-6 pt-2 text-center">
        <p className="text-xs text-[#9c968a]" data-testid="storefront-brand-footer">
          {t("store.poweredBy")} <Brand country={business.country} className="text-[#5C574F]" />
        </p>
        <p className="mt-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-[#9c968a]">
          <a href="/mentions-legales" className="hover:text-[#E85D04]" data-testid="storefront-legal-mentions">Mentions légales</a>
          <span className="text-[#D8D2C8]">·</span>
          <a href="/cgv" className="hover:text-[#E85D04]" data-testid="storefront-legal-cgv">CGV</a>
          <span className="text-[#D8D2C8]">·</span>
          <a href="/politique-confidentialite" className="hover:text-[#E85D04]" data-testid="storefront-legal-confidentialite">Confidentialité</a>
          <span className="text-[#D8D2C8]">·</span>
          <a href="/politique-remboursement" className="hover:text-[#E85D04]" data-testid="storefront-legal-remboursement">Remboursement</a>
        </p>
      </div>

      {/* Sticky cart bar */}
      <AnimatePresence>
        {count > 0 && !checkout && (
          <motion.div initial={{ y: 80 }} animate={{ y: 0 }} exit={{ y: 80 }} className="fixed bottom-0 left-0 right-0 z-40 p-4">
            <button data-testid="open-checkout-btn" onClick={() => setCheckout(true)} className="max-w-lg md:max-w-2xl mx-auto w-full h-14 rounded-2xl bg-[#E85D04] text-white font-bold flex items-center justify-between px-5 shadow-xl active:scale-95 transition-transform">
              <span className="flex items-center gap-2"><ShoppingCart className="h-5 w-5" /> {t("store.cartItems", { count })}</span>
              <span>{money(total, currency)} • {t("store.order")}</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {checkout && (
          <Checkout slug={slug} country={business.country} interacEnabled={business.interac_enabled} zones={business.delivery_zones || []} cartItems={cartItems} total={total} currency={currency} salesPolicy={business.sales_policy ? (business.sales_policy.content[curLang] || business.sales_policy.content.fr) : null} onChangeQty={changeQty} onRemove={removeLine} onClose={() => setCheckout(false)} onDone={(orderId) => { saveMyOrder({ orderId, slug, storeName: business.name }); navigate(`/commande/${orderId}`); }} />
        )}
      </AnimatePresence>

      <CustomerAssistant slug={slug} businessName={business.name} whatsapp={business.whatsapp} />

      <AnimatePresence>
        {detail && (
          <ProductDetailSheet
            product={detail}
            currency={currency}
            slug={slug}
            inCart={qtyForProduct(detail.product_id)}
            reviews={business.reviews_summary}
            suggestions={products.filter((s) => s.product_id !== detail.product_id && s.stock_qty > 0).slice(0, 8)}
            onAddQty={addToCart}
            onOpen={(s) => setDetail(s)}
            onClose={() => setDetail(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {lightbox && <ImageLightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
      </AnimatePresence>
    </div>
  );
}

const ORDER_OPTIONS = [
  { id: "online", tKey: "online", icon: CreditCard, mode: "livraison", slots: 2 },
  { id: "on_delivery", tKey: "on_delivery", icon: Truck, mode: "livraison", slots: 2 },
  { id: "on_pickup", tKey: "on_pickup", icon: Store, mode: "retrait", slots: 1 },
];

// Provinces/territoires canadiens (code 2 lettres = valeur exigée par FlagShip).
const CA_PROVINCES = [
  { code: "AB", name: "Alberta" }, { code: "BC", name: "Colombie-Britannique" },
  { code: "MB", name: "Manitoba" }, { code: "NB", name: "Nouveau-Brunswick" },
  { code: "NL", name: "Terre-Neuve-et-Labrador" }, { code: "NS", name: "Nouvelle-Écosse" },
  { code: "NT", name: "Territoires du Nord-Ouest" }, { code: "NU", name: "Nunavut" },
  { code: "ON", name: "Ontario" }, { code: "PE", name: "Île-du-Prince-Édouard" },
  { code: "QC", name: "Québec" }, { code: "SK", name: "Saskatchewan" }, { code: "YT", name: "Yukon" },
];

// Première lettre du code postal canadien → province (déduction fiable).
const CA_FSA_PROVINCE = {
  A: "NL", B: "NS", C: "PE", E: "NB", G: "QC", H: "QC", J: "QC",
  K: "ON", L: "ON", M: "ON", N: "ON", P: "ON", R: "MB", S: "SK",
  T: "AB", V: "BC", X: "NT", Y: "YT",
};

// Formate un code postal canadien en « A1A 1A1 » (majuscules + espace au bon endroit).
function formatCAPostal(v) {
  const s = (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  return s.length > 3 ? `${s.slice(0, 3)} ${s.slice(3)}` : s;
}

const CA_POSTAL_RE = /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/;

function SlotPicker({ index, value, onChange, days }) {
  const { t } = useTranslation();
  return (
    <div className="bg-[#F9F6F0] rounded-xl p-3" data-testid={`slot-picker-${index}`}>
      <p className="text-xs font-semibold text-[#5C574F] mb-2 flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-[#E85D04]" /> {t("checkout.slotN", { n: index + 1 })}</p>
      <div className="grid grid-cols-2 gap-2">
        <select
          data-testid={`slot-date-${index}`}
          value={value.date}
          onChange={(e) => onChange({ ...value, date: e.target.value })}
          className="h-11 bg-white rounded-lg px-2 text-sm outline-none border border-[#E5E0D8] focus:border-[#E85D04]"
        >
          <option value="">{t("checkout.dateP")}</option>
          {days.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
        </select>
        <select
          data-testid={`slot-time-${index}`}
          value={value.time}
          onChange={(e) => onChange({ ...value, time: e.target.value })}
          className="h-11 bg-white rounded-lg px-2 text-sm outline-none border border-[#E5E0D8] focus:border-[#E85D04]"
        >
          <option value="">{t("checkout.timeP")}</option>
          {TIME_SLOTS.map((time) => <option key={time} value={time}>{time}</option>)}
        </select>
      </div>
    </div>
  );
}

function Checkout({ slug, country, interacEnabled = false, zones = [], cartItems, total, currency, salesPolicy, onChangeQty, onRemove, onClose, onDone }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ customer_name: "", customer_phone: "", customer_email: "", note: "", address: "", city: "", province: "", postal: "" });
  const [option, setOption] = useState("online");
  const [deliveryZone, setDeliveryZone] = useState("");
  const [carrierZones, setCarrierZones] = useState([]);
  const [flagshipRates, setFlagshipRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const isCarrierMode = country === "CA";
  const [channel, setChannel] = useState("whatsapp");
  const [slots, setSlots] = useState([{ date: "", time: "" }, { date: "", time: "" }]);
  const [busy, setBusy] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [policyAccepted, setPolicyAccepted] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [payOrder, setPayOrder] = useState(null);
  const [successOrder, setSuccessOrder] = useState(null);
  const days = useMemo(() => nextDays(14), []);
  const paymentOptions = useMemo(() => {
    const base = [...ORDER_OPTIONS];
    if (country === "CA" && interacEnabled) {
      base.splice(1, 0, { id: "interac_etransfer", tKey: "interac", icon: Banknote, mode: "livraison", slots: 2 });
    }
    return base;
  }, [country, interacEnabled]);
  const opt = paymentOptions.find((o) => o.id === option) || paymentOptions[0];
  const neededSlots = opt.slots;
  const useFlagship = isCarrierMode && flagshipRates.length > 0;
  const zoneOptions = useFlagship ? flagshipRates : (isCarrierMode ? carrierZones : zones);
  const showZones = opt.mode === "livraison" && zoneOptions.length > 0;
  const selectedZone = zoneOptions.find((z) => z.name === deliveryZone);
  const needsAddress = ["CA", "FR"].includes(country) && opt.mode === "livraison";
  const addressComplete = form.city.trim() && form.province.trim() && form.postal.trim();

  // Canada : récupère les transporteurs du marchand avec leur prix calculé selon le poids du panier.
  useEffect(() => {
    if (!isCarrierMode || !cartItems.length) { setCarrierZones([]); return; }
    let cancelled = false;
    api.post(`/public/store/${slug}/shipping-quote`, {
      items: cartItems.map((p) => ({ product_id: p.product_id, qty: p.qty, size: p.size || "" })),
    }).then(({ data }) => { if (!cancelled) setCarrierZones(data.carriers || []); }).catch(() => {});
    return () => { cancelled = true; };
  }, [isCarrierMode, slug, cartItems]);

  // Canada : tarifs LIVE FlagShip (UPS/FedEx/Purolator…) une fois l'adresse du client renseignée.
  useEffect(() => {
    if (!isCarrierMode || opt.mode !== "livraison" || !cartItems.length || !addressComplete) { setFlagshipRates([]); return; }
    let cancelled = false;
    setLoadingRates(true);
    const h = setTimeout(() => {
      api.post(`/public/store/${slug}/flagship-rates`, {
        items: cartItems.map((p) => ({ product_id: p.product_id, qty: p.qty })),
        to_address: form.address.trim(), to_city: form.city.trim(), to_province: form.province.trim(),
        to_postal: form.postal.trim(), to_phone: form.customer_phone,
      }).then(({ data }) => { if (!cancelled) { setFlagshipRates(data.carriers || []); setDeliveryZone(""); } })
        .catch(() => { if (!cancelled) setFlagshipRates([]); })
        .finally(() => { if (!cancelled) setLoadingRates(false); });
    }, 600);
    return () => { cancelled = true; clearTimeout(h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCarrierMode, opt.mode, slug, cartItems, form.address, form.city, form.province, form.postal]);

  const setSlot = (i, v) => setSlots((s) => s.map((x, idx) => (idx === i ? v : x)));

  const submit = async () => {
    if (!cartItems.length) { toast.error(t("checkout.emptyCart")); onClose(); return; }
    if (!form.customer_name || !form.customer_phone) { toast.error(t("checkout.namePhoneRequired")); return; }
    if (channel === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.customer_email)) {
      toast.error(t("checkout.validEmail"));
      return;
    }
    const chosen = slots.slice(0, neededSlots);
    if (chosen.some((s) => !s.date || !s.time)) {
      toast.error(neededSlots > 1 ? t("checkout.chooseSlotsN") : t("checkout.chooseSlot"));
      return;
    }
    if (salesPolicy && !policyAccepted) {
      toast.error(t("checkout.policyRequired"));
      return;
    }
    if (showZones && !deliveryZone) {
      toast.error(isCarrierMode ? t("checkout.carrierRequired") : t("checkout.zoneRequired"));
      return;
    }
    if (needsAddress && (!form.address.trim() || !form.city.trim() || !form.province.trim() || !form.postal.trim())) {
      toast.error(t("checkout.addressRequired"));
      return;
    }
    if (needsAddress && country === "CA" && !CA_POSTAL_RE.test(form.postal.trim())) {
      toast.error(t("checkout.postalInvalid"));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/public/store/${slug}/orders`, {
        customer_name: form.customer_name, customer_phone: form.customer_phone, note: form.note,
        contact_channel: channel, customer_email: form.customer_email,
        mode: opt.mode, payment_type: option, slots: chosen, delivery_zone: (showZones && !selectedZone?.id) ? deliveryZone : null,
        flagship_service: selectedZone?.id || null,
        delivery_address: needsAddress ? form.address.trim() : "",
        delivery_city: needsAddress ? form.city.trim() : "",
        delivery_province: needsAddress ? form.province.trim() : "",
        delivery_postal: needsAddress ? form.postal.trim() : "",
        items: cartItems.map((p) => ({ product_id: p.product_id, qty: p.qty, size: p.size || "" })),
      });
      // Paiement en ligne : on encaisse tout de suite dans la boutique (pas d'étape "en attente de paiement").
      if (option === "online" && data.status === "a_payer") {
        const { data: full } = await api.get(`/public/orders/${data.order_id}`);
        setPayOrder(full);
        return;
      }
      toast.success(t("checkout.orderSent"));
      onDone(data.order_id);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/40 flex items-end justify-center" onClick={onClose}>
      <motion.div initial={{ y: 60 }} animate={{ y: 0 }} exit={{ y: 60 }} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg rounded-t-3xl p-5 max-h-[92vh] overflow-y-auto no-scrollbar">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold">{successOrder ? "" : payOrder ? t("checkout.payTitle") : t("checkout.title")}</h3>
          <button onClick={successOrder ? () => onDone(successOrder.order_id) : payOrder ? () => onDone(payOrder.order_id) : onClose} className="h-9 w-9 rounded-full bg-[#F9F6F0] flex items-center justify-center"><X className="h-5 w-5" /></button>
        </div>

        {successOrder ? (
          <PaymentSuccess order={successOrder} onContinue={() => onDone(successOrder.order_id)} />
        ) : payOrder ? (
          <div className="space-y-4" data-testid="checkout-payment-step">
            <div className="bg-[#F9F6F0] rounded-xl p-4 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-[#5C574F]">{t("tracking.subtotal")}</span><span className="font-semibold">{money(payOrder.subtotal, payOrder.currency || currency)}</span></div>
              {payOrder.delivery_fee > 0 && <div className="flex justify-between"><span className="text-[#5C574F]">{t("tracking.delivery")}</span><span className="font-semibold">{money(payOrder.delivery_fee, payOrder.currency || currency)}</span></div>}
              {(payOrder.tax_lines || []).map((tx) => (
                <div key={tx.name} className="flex justify-between text-[#5C574F]"><span>{tx.name}</span><span>{money(tx.amount, payOrder.currency || currency)}</span></div>
              ))}
              <div className="border-t border-[#E5E0D8] pt-1.5 flex justify-between font-display font-bold text-[#E85D04]"><span>{t("tracking.total")}</span><span>{money(payOrder.total, payOrder.currency || currency)}</span></div>
            </div>
            <PaymentPanel order={payOrder} onPaid={() => setSuccessOrder(payOrder)} />
          </div>
        ) : (
        <>
        <div className="bg-[#F9F6F0] rounded-xl p-4 space-y-2 mb-4">
          {cartItems.map((p) => (
            <div key={p.key} className="flex items-center justify-between gap-2 text-sm" data-testid={`checkout-line-${p.key}`}>
              <div className="min-w-0 flex-1">
                <span className="text-[#5C574F] block truncate">{p.name}</span>
                {p.size && <span className="text-[11px] font-semibold text-[#1B4332] bg-[#1B4332]/10 px-1.5 py-0.5 rounded" data-testid={`checkout-line-size-${p.key}`}>{t("checkout.size", { defaultValue: "Taille" })} {p.size}</span>}
              </div>
              <div className="flex items-center border border-[#E5E0D8] rounded-lg bg-white shrink-0">
                <button data-testid={`line-minus-${p.key}`} onClick={() => onChangeQty(p.key, -1)} className="h-8 w-8 flex items-center justify-center text-[#5C574F]"><Minus className="h-3.5 w-3.5" /></button>
                <span className="w-7 text-center font-medium">{p.qty}</span>
                <button data-testid={`line-plus-${p.key}`} onClick={() => onChangeQty(p.key, 1)} className="h-8 w-8 flex items-center justify-center text-[#5C574F]"><Plus className="h-3.5 w-3.5" /></button>
              </div>
              <span className="font-medium w-20 text-right shrink-0">{money(p.price * p.qty, currency)}</span>
              <button data-testid={`line-remove-${p.key}`} onClick={() => onRemove(p.key)} className="h-7 w-7 rounded-full bg-[#D00000]/10 text-[#D00000] flex items-center justify-center shrink-0"><X className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          <div className="border-t border-[#E5E0D8] pt-2 flex justify-between font-display font-bold text-[#E85D04]"><span>{t("checkout.subtotal")}</span><span>{money(total, currency)}</span></div>
          {opt.mode === "livraison" && <p className="text-xs text-[#9c968a]">{t("checkout.deliveryFeeNote")}</p>}
        </div>

        <div className="space-y-3">
          <input data-testid="checkout-name" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder={t("checkout.yourName")} className="w-full bg-[#F9F6F0] rounded-xl px-4 h-12 outline-none border border-transparent focus:border-[#E85D04]" />
          <input data-testid="checkout-phone" value={form.customer_phone} onChange={(e) => { setForm({ ...form, customer_phone: e.target.value }); setPhoneVerified(false); }} placeholder={t("checkout.phonePh")} className="w-full bg-[#F9F6F0] rounded-xl px-4 h-12 outline-none border border-transparent focus:border-[#E85D04]" />
          <PhoneVerify phone={form.customer_phone} verified={phoneVerified} onVerified={() => setPhoneVerified(true)} hint={t("checkout.phoneHint")} country={country} />

          <div className="space-y-2">
            <p className="text-sm font-semibold text-[#5C574F]">{t("checkout.contactPref")}</p>
            <div className="grid grid-cols-3 gap-2">
              {[["whatsapp", t("checkout.whatsapp"), MessageCircle], ["gsm", t("checkout.call"), PhoneCall], ["email", t("checkout.email"), Mail]].map(([id, label, Icon]) => {
                const active = channel === id;
                return (
                  <button
                    key={id}
                    type="button"
                    data-testid={`channel-${id}`}
                    onClick={() => setChannel(id)}
                    className={`h-16 rounded-xl flex flex-col items-center justify-center gap-1 border-2 transition-colors ${active ? "border-[#E85D04] bg-[#E85D04]/5 text-[#E85D04]" : "border-[#E5E0D8] bg-[#F9F6F0] text-[#5C574F]"}`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs font-semibold">{label}</span>
                  </button>
                );
              })}
            </div>
            {channel === "email" && (
              <input data-testid="checkout-email" type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} placeholder={t("checkout.yourEmail")} className="w-full bg-[#F9F6F0] rounded-xl px-4 h-12 outline-none border border-transparent focus:border-[#E85D04]" />
            )}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-[#5C574F]">{t("checkout.orderMode")}</p>
            {paymentOptions.map((o) => {
              const Icon = o.icon;
              const active = option === o.id;
              const disabled = o.id === "on_delivery" && ["CA", "FR"].includes(country);
              return (
                <button
                  key={o.id}
                  data-testid={`option-${o.id}`}
                  disabled={disabled}
                  onClick={() => !disabled && setOption(o.id)}
                  className={`w-full text-left rounded-xl p-3 flex items-center gap-3 border-2 transition-colors ${disabled ? "border-[#E5E0D8] bg-[#F2EFE9] opacity-50 cursor-not-allowed" : active ? "border-[#E85D04] bg-[#E85D04]/5" : "border-[#E5E0D8] bg-[#F9F6F0]"}`}
                >
                  <span className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${active && !disabled ? "bg-[#E85D04] text-white" : "bg-white text-[#5C574F]"}`}><Icon className="h-5 w-5" /></span>
                  <span className="min-w-0">
                    <span className="block font-semibold text-sm leading-tight">{t(`checkout.opt_${o.tKey}_t`)}</span>
                    <span className="block text-xs text-[#9c968a]">{disabled ? t("checkout.notAvailableCountry") : t(`checkout.opt_${o.tKey}_d`)}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {needsAddress && (
            <div className="space-y-2" data-testid="address-section">
              <p className="text-sm font-semibold text-[#5C574F]">{t("checkout.shippingAddress")}</p>
              <input data-testid="checkout-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder={t("checkout.addressPh")} className="w-full bg-[#F9F6F0] rounded-xl px-4 h-12 outline-none border border-transparent focus:border-[#E85D04]" />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="checkout-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder={t("checkout.cityPh")} className="bg-[#F9F6F0] rounded-xl px-4 h-12 outline-none border border-transparent focus:border-[#E85D04]" />
                {country === "CA" ? (
                  <select data-testid="checkout-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })}
                    className={`bg-[#F9F6F0] rounded-xl px-4 h-12 outline-none border border-transparent focus:border-[#E85D04] ${form.province ? "text-[#1A1A1A]" : "text-[#9c968a]"}`}>
                    <option value="">{t("checkout.provinceSelect")}</option>
                    {CA_PROVINCES.map((p) => <option key={p.code} value={p.code} className="text-[#1A1A1A]">{p.name} ({p.code})</option>)}
                  </select>
                ) : (
                  <input data-testid="checkout-province" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} placeholder={t("checkout.provincePh")} className="bg-[#F9F6F0] rounded-xl px-4 h-12 outline-none border border-transparent focus:border-[#E85D04]" />
                )}
              </div>
              {country === "CA" ? (
                <>
                  <input data-testid="checkout-postal" value={form.postal} inputMode="text" autoCapitalize="characters" maxLength={7}
                    onChange={(e) => {
                      const postal = formatCAPostal(e.target.value);
                      const first = postal.replace(/[^A-Z0-9]/g, "")[0];
                      const autoProv = first ? CA_FSA_PROVINCE[first] : null;
                      setForm((f) => ({ ...f, postal, province: (autoProv && !f.province) ? autoProv : f.province }));
                    }}
                    placeholder={t("checkout.postalPh")}
                    className={`w-full bg-[#F9F6F0] rounded-xl px-4 h-12 outline-none border focus:border-[#E85D04] ${form.postal && !CA_POSTAL_RE.test(form.postal) ? "border-red-400" : "border-transparent"}`} />
                  {form.postal && !CA_POSTAL_RE.test(form.postal) && (
                    <p className="text-xs text-red-500" data-testid="postal-error">{t("checkout.postalInvalid")}</p>
                  )}
                </>
              ) : (
                <input data-testid="checkout-postal" value={form.postal} onChange={(e) => setForm({ ...form, postal: e.target.value })} placeholder={t("checkout.postalPh")} className="w-full bg-[#F9F6F0] rounded-xl px-4 h-12 outline-none border border-transparent focus:border-[#E85D04]" />
              )}
            </div>
          )}

          {isCarrierMode && opt.mode === "livraison" && !addressComplete && (
            <p className="text-xs text-[#9c968a]" data-testid="flagship-address-hint">{t("checkout.enterAddressForRates")}</p>
          )}
          {isCarrierMode && opt.mode === "livraison" && addressComplete && loadingRates && (
            <p className="text-xs text-[#5C574F] flex items-center gap-2" data-testid="flagship-loading"><span className="inline-block h-3 w-3 rounded-full border-2 border-[#E85D04] border-t-transparent animate-spin" /> {t("checkout.loadingRates")}</p>
          )}

          {showZones && (
            <div className="space-y-2" data-testid="zone-section">
              <p className="text-sm font-semibold text-[#5C574F] flex items-center gap-2">
                {isCarrierMode ? t("checkout.carrierLabel") : t("checkout.deliveryZone")}
                {useFlagship && <span className="text-[10px] font-bold uppercase tracking-wide text-[#1B4332] bg-[#1B4332]/10 px-1.5 py-0.5 rounded" data-testid="flagship-live-badge">{t("checkout.liveRates")}</span>}
              </p>
              <select
                data-testid="checkout-zone"
                value={deliveryZone}
                onChange={(e) => setDeliveryZone(e.target.value)}
                className="w-full h-12 bg-[#F9F6F0] rounded-xl px-3 text-sm outline-none border border-transparent focus:border-[#E85D04]"
              >
                <option value="">{isCarrierMode ? t("checkout.chooseCarrier") : t("checkout.chooseZone")}</option>
                {zoneOptions.map((z) => (
                  <option key={z.id || z.name} value={z.name}>{z.name} — {money(z.fee, currency)}{z.transit_time ? ` · ${z.transit_time} j` : ""}</option>
                ))}
              </select>
              {selectedZone && (
                <p className="text-xs text-[#5C574F]" data-testid="zone-fee-line">{t("checkout.deliveryFee")} : <span className="font-semibold">{money(selectedZone.fee, currency)}</span></p>
              )}
            </div>
          )}

          <div className="space-y-2" data-testid="slots-section">
            <p className="text-sm font-semibold text-[#5C574F]">
              {opt.mode === "retrait" ? t("checkout.pickupSlot") : t("checkout.chooseSlots")}
            </p>
            {Array.from({ length: neededSlots }).map((_, i) => (
              <SlotPicker key={`slot-${i}`} index={i} value={slots[i]} onChange={(v) => setSlot(i, v)} days={days} />
            ))}
          </div>

          <textarea data-testid="checkout-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("checkout.notePh")} rows={2} className="w-full bg-[#F9F6F0] rounded-xl p-3 outline-none border border-transparent focus:border-[#E85D04] resize-none" />
        </div>

        <button data-testid="submit-order-btn" onClick={submit} disabled={busy || (salesPolicy && !policyAccepted)} className="w-full h-14 mt-4 rounded-2xl bg-[#E85D04] text-white font-bold active:scale-95 transition-transform disabled:opacity-60">
          {busy ? t("common.sending") : t("checkout.send")}
        </button>

        {salesPolicy && (
          <div className="mt-3 flex items-start gap-2.5" data-testid="sales-policy-consent">
            <input
              id="sales-policy-check"
              type="checkbox"
              data-testid="sales-policy-checkbox"
              checked={policyAccepted}
              onChange={(e) => setPolicyAccepted(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[#E85D04] cursor-pointer"
            />
            <label htmlFor="sales-policy-check" className="text-xs text-[#5C574F] leading-relaxed cursor-pointer">
              {t("checkout.policyAccept")} —{" "}
              <button
                type="button"
                data-testid="sales-policy-link"
                onClick={(e) => { e.preventDefault(); setPolicyOpen(true); }}
                className="font-semibold text-[#E85D04] underline"
              >
                {t("checkout.policyLink")}
              </button>
            </label>
          </div>
        )}
        </>
        )}
      </motion.div>

      {salesPolicy && policyOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={(e) => { e.stopPropagation(); setPolicyOpen(false); }}
          data-testid="sales-policy-modal"
        >
          <div
            className="bg-white w-full max-w-lg rounded-t-3xl sm:rounded-3xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-[#E5E0D8]">
              <h3 className="font-display text-lg font-bold">{t("checkout.policyTitle")}</h3>
              <button data-testid="sales-policy-close" onClick={() => setPolicyOpen(false)} className="h-9 w-9 rounded-full bg-[#F9F6F0] flex items-center justify-center"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-5 overflow-y-auto text-sm text-[#5C574F] leading-relaxed whitespace-pre-line" data-testid="sales-policy-content">
              {salesPolicy}
            </div>
            <div className="p-4 border-t border-[#E5E0D8]">
              <button
                data-testid="sales-policy-accept-btn"
                onClick={() => { setPolicyAccepted(true); setPolicyOpen(false); }}
                className="w-full h-12 rounded-2xl bg-[#1B4332] text-white font-semibold active:scale-95 transition-transform"
              >
                {t("checkout.policyAccept")}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}

function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center text-[#5C574F] px-6 text-center">{children}</div>;
}
